const HMR_KEY = '__render_state__';

export type RendererEntry = {
  render: (args?: any) => unknown;
  shouldSuppressRenderError?: () => boolean;
};

export type RenderState = {
  renderers: Record<string, RendererEntry>;
  frontBuffer: Map<string, any>;
  backBuffer: Map<string, any>;
  renderScheduled: boolean;
  rafId: number;
};

function createState(): RenderState {
  return {
    renderers: {},
    frontBuffer: new Map(),
    backBuffer: new Map(),
    renderScheduled: false,
    rafId: 0,
  };
}

function ensureRenderState(): RenderState {
  const g = globalThis as any;
  if (!g[HMR_KEY]) g[HMR_KEY] = createState();
  return g[HMR_KEY];
}


function renderPerfNow() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}


function isCrossSectionMarkerSyncType(type: unknown) {
  return (
    type === 'markerMove' ||
    type === 'markerMoveDone' ||
    type === 'aorticCalcium_move'
  );
}

function shouldSkipStaleCrossSectionRender(
  state: RenderState,
  rendererId: string,
  params: any,
) {
  if (!rendererId.startsWith('CrossSection.')) return false;
  const type = params?.type;
  if (!isCrossSectionMarkerSyncType(type)) return false;

  const pending = state.backBuffer.get(rendererId);
  if (!pending || !isCrossSectionMarkerSyncType(pending?.type)) return false;

  const currentScheduledAt = Number(params?.__renderScheduledAt);
  const pendingScheduledAt = Number(pending?.__renderScheduledAt);
  const pendingIsNewer =
    !Number.isFinite(currentScheduledAt) ||
    !Number.isFinite(pendingScheduledAt) ||
    pendingScheduledAt >= currentScheduledAt;

  if (!pendingIsNewer) return false;

  // While dragging, obsolete markerMove previews are expensive because each
  // CrossSection render can take ~10-15ms. If a newer preview or final commit
  // is already queued for the same panel, drop the stale one and let the next
  // frame render only the latest index. Final markerMoveDone is never skipped
  // unless an even newer final commit for the same panel is queued.
  if (type === 'markerMove') return true;
  if (type === 'aorticCalcium_move') return pending.type === 'aorticCalcium_move';
  if (type === 'markerMoveDone') return pending.type === 'markerMoveDone';
  return false;
}


function isGroupCrossSectionMarkerPreview(params: any) {
  return params?.type === 'markerMove' && params?.markerGroupPreview === true;
}

function deferCrossSectionPreviewRender(
  state: RenderState,
  rendererId: string,
  params: any,
) {
  const pending = state.backBuffer.get(rendererId);
  if (pending && isCrossSectionMarkerSyncType(pending?.type)) return;
  state.backBuffer.set(rendererId, params);
}

function shouldKeepPendingCrossSectionCommit(
  rendererId: string,
  pending: any,
  next: any,
) {
  if (!rendererId.startsWith('CrossSection.')) return false;
  if (pending?.type !== 'markerMoveDone') return false;
  return next?.type === 'markerMove' || next?.type === 'updateRenderWindow';
}

if (import.meta && (import.meta as any).webpackHot) {
  (import.meta as any).webpackHot.dispose(() => {
    const g = globalThis as any;
    delete g[HMR_KEY];
  });
}


export const renderers = ensureRenderState().renderers;

export function registerRenderer(rendererId: string, renderer: RendererEntry) {
  const state = ensureRenderState();
  state.renderers[rendererId] = renderer;

  return () => {
    if (state.renderers[rendererId] === renderer) {
      delete state.renderers[rendererId];
    }
  };
}

export function scheduleRender(rendererId: string, args?: any) {
  const state = ensureRenderState();
  const scheduleAt = renderPerfNow();
  const nextArgs = {
    ...args,
    __renderScheduledAt: scheduleAt,
  };
  const pendingArgs = state.backBuffer.get(rendererId);
  if (!shouldKeepPendingCrossSectionCommit(rendererId, pendingArgs, nextArgs)) {
    state.backBuffer.set(rendererId, nextArgs);
  }
  // needsRender.set(rendererId, args);
  if (!state.renderScheduled) {
    state.renderScheduled = true;
    state.rafId = requestAnimationFrame(() => {
      const tmp = state.frontBuffer;
      state.frontBuffer = state.backBuffer;
      state.backBuffer = tmp;
      state.backBuffer.clear();

      let renderedGroupCrossSectionPreview = false;
      Array.from(state.frontBuffer.entries()).forEach(([rid, params]) => {
        if (shouldSkipStaleCrossSectionRender(state, rid, params)) {
          return;
        }

        // A group marker drag can enqueue 3 CrossSection markerMove previews.
        // Rendering all 3 in a single RAF blocks the main thread for ~60ms.
        // Keep only one group preview render per RAF and carry the remaining
        // latest preview requests to the next frame. Final markerMoveDone is
        // never deferred, so mouse-up can cut in between preview frames.
        if (isGroupCrossSectionMarkerPreview(params) && renderedGroupCrossSectionPreview) {
          deferCrossSectionPreviewRender(state, rid, params);
          return;
        }

        const renderer = renderers[rid];
        if (!renderer) return;
        if (isGroupCrossSectionMarkerPreview(params)) renderedGroupCrossSectionPreview = true;
        try {
          renderer.render(params);
        } catch (err) {
          if (renderer.shouldSuppressRenderError?.()) {
            console.warn('[renderState] server front_data render skipped', {
              rendererId: rid,
              params,
              err,
            });
            return;
          }
          throw err;
        }
      });
      state.frontBuffer.clear();

      state.renderScheduled = false;

      if (state.backBuffer.size > 0) {
        scheduleRender('dummy');
      }
    });
  }
}

if ((import.meta as any).webpackHot) {
  (import.meta as any).webpackHot.dispose(() => {
    const state = ensureRenderState();
    if (state.rafId) cancelAnimationFrame(state.rafId);
    state.rafId = 0;
    state.frontBuffer.clear();
    state.backBuffer.clear();
  });
}
